import { Button } from 'components/atoms'
import moment from 'moment'
import * as React from 'react'
import 'react-dates/initialize'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { reservationService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import ReceiptPreview from './ReceiptPreview'
import ReciptModalBody from './ReceiptModal'
import ReservationCancelModalBody from '../../../organisms/Reservations/ReservationCancelModalBody'
import LinkButton from 'components/atoms/LinkButton'

declare var window: interfaces.IWindow
interface IProps {
  reservation: any
  receiptId: number | null
  user: interfaces.IUser
  isAgentCreatePost: boolean
  setReservation?: (newReservation: any) => void
  page: 'check_reservation' | 'check_tour' | 'assign_guide'
  // 予約周り以外で表示したいボタンがある際に設定
  buttons?: React.ReactElement
}

const AboutReservation: React.FC<IProps> = props => {
  const [reservation, setReservation] = React.useState<any>(props.reservation)
  const [receiptId, setReceiptId] = React.useState<number>(props.receiptId)
  const [receipt, setReceipt] = React.useState<any>(null)
  const [loading, setLoading] = React.useState<any>(null)
  const createReceiptFormRef = React.useRef(null)

  React.useEffect(() => {
    const get = async () => {
      if (receiptId) {
        setLoading(true)
        const { receipt: receiptData } = await reservationService
          .getReceipt(receiptId)
          .finally(() => setLoading(false))
        setReceipt(receiptData)
      }
    }
    get()
  }, [receiptId])

  const handleUpdated = (newReservation, flush) => {
    setReservation(newReservation)
    if (props.setReservation) {
      props.setReservation(newReservation)
    }
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }

  const approveReservation = React.useCallback(async id => {
    const { reservation: updated, flush } = await reservationService.approveReservation(id)
    handleUpdated(updated, flush)
  }, [])

  const declineReservation = React.useCallback(async id => {
    const { reservation: updated, flush } = await reservationService.declineReservation(id)
    handleUpdated(updated, flush)
  }, [])

  const showCancelModal = id => {
    window.globalModal.showModal({
      title: I18n.t('cancel.cancel_reservation'),
      body: (
        <ReservationCancelModalBody
          reservationId={id}
          reservationStatus={reservation.workflow_state_enum_value}
        />
      ),
    })
  }
  const isPassedReservation = ({ end_at }) => {
    const date = I18n.locale === 'en' ? moment(end_at) : moment(end_at, 'YYYY年MM月DD日 HH:mm')

    return date.isBefore()
  }

  const getLabelAndClickEvent = (state, page, id) => {
    if (state === 'approved' && page !== 'assign_guide') {
      return {
        handleClick: () => approveReservation(id),
        label: I18n.t('generic.approve'),
      }
    } else if (state === 'declined') {
      return {
        handleClick: () => declineReservation(id),
        label: I18n.t('generic.decline'),
      }
    } else if (state === 'canceled') {
      return props.user.agent && props.reservation.cancel_application
        ? {
            handleClick: () => (window.location.href = '/agent/cancel_applications'),
            label: I18n.t('reservation.cancel_application'),
          }
        : {
            handleClick: () => showCancelModal(id),
            label: I18n.t('cancel.cancel_reservation'),
          }
    }
    return null
  }

  const renderNextWorkFlowStateButtons = React.useCallback(() => {
    const hideButtons =
      isPassedReservation(reservation) ||
      !reservation.next_work_flow_state ||
      (props.isAgentCreatePost && props.user.guide)

    if (hideButtons) {
      return null
    }

    return reservation.next_work_flow_state.map((state, index) => {
      const labelAndClickEvent = getLabelAndClickEvent(state, props.page, reservation.id)
      if (labelAndClickEvent) {
        const { handleClick, label } = labelAndClickEvent
        return (
          <Button key={`${reservation.id}-${index}`} small={true} handleClick={handleClick}>
            {label}
          </Button>
        )
      }
    })
  }, [reservation])
  const createReceipt = (id: number) => {
    if (id) {
      const el = document.getElementById('ReceiptPreview')
      import('html2canvas').then(({ default: html2canvas }) => {
        html2canvas(el).then(capture => {
          const imgData = capture.toDataURL('image/png')
          import('jspdf').then(({ jsPDF }) => {
            const doc = new jsPDF()
            const width = doc.internal.pageSize.getWidth()
            doc.addImage(imgData, 'PNG', 0, 0, width, 0)
            doc.save(`receipt-${id}.pdf`)
          })
        })
      })
    }
  }
  const showAboutReservationModal = (id: number) => {
    window.globalModal.showModal({
      body: (
        <AboutReservation
          reservation={reservation}
          receiptId={id}
          user={props.user}
          setReservation={setReservation}
          isAgentCreatePost={props.isAgentCreatePost}
          page="check_reservation"
        />
      ),
      closeText: I18n.t('generic.cancel'),
    })
  }

  const showCreateReceiptModal = async () => {
    if (receiptId) {
      createReceipt(receiptId)
    } else {
      window.globalModal.showModal({
        title: I18n.t('receipt.create_receipt'),
        body: (
          <ReciptModalBody
            reservationId={reservation.id}
            createReceiptFormRef={createReceiptFormRef}
            setReceiptId={setReceiptId}
            handleUpdated={showAboutReservationModal}
          />
        ),
        closeText: I18n.t('generic.cancel'),
        submitText: I18n.t('generic.create'),
        handleSubmit: () => createReceiptFormRef.current.handleFormSubmit(),
      })
    }
  }
  const showReceiptButton = React.useMemo(() => {
    const accesible =
      props.user.guest && reservation.receipt_accessible && !!reservation.next_work_flow_state
    return accesible
  }, [
    props.user.guest,
    reservation.end_at,
    reservation.receipt_accesible,
    reservation.next_work_flow_state,
  ])
  return (
    <S.Reservation>
      <h2>{I18n.t('reservation.about_reservation')}</h2>
      <dl>
        {reservation.no && (
          <>
            <dt>No</dt>
            <dd>{reservation.no}</dd>
            <dd>
              <LinkButton href={`/reservations/${reservation.slug}`} newTab={true}>
                {I18n.t('generic.show_detail')}
              </LinkButton>
            </dd>
          </>
        )}
        <dt>{I18n.t('generic.status')}</dt>
        <dd>{props.reservation.workflow_state || 'Direct'}</dd>
        <dt>{I18n.t('post.post_name')}</dt>
        <dd>{props.reservation.post.name}</dd>
        <dt>{I18n.t('generic.price')}</dt>
        <dd>{props.reservation.charge_price || props.reservation.price}</dd>
        <dt>{I18n.t('generic.period')}</dt>
        <dd>
          {props.reservation.start_at} 〜 {props.reservation.end_at}
        </dd>
      </dl>
      {receipt && (
        <div className="receipt_wrapper">
          <ReceiptPreview receipt={receipt} />
        </div>
      )}
      <S.Buttons>
        {props.buttons && props.buttons}
        {renderNextWorkFlowStateButtons()}
        {showReceiptButton && (
          <Button handleClick={showCreateReceiptModal} disabled={loading}>
            {I18n.t(receipt ? 'receipt.show_receipt' : 'receipt.create_receipt')}
          </Button>
        )}
      </S.Buttons>
    </S.Reservation>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.Buttons = styled.div`
  display: flex;
  margin-top: 12px;

  .Button {
    width: 50%;
    height: 40px;
  }
`
S.Reservation = styled.div`
  width: 100%;
  max-width: 720px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding: 16px 12px 12px;
    width: auto;
  }
  .receipt_wrapper {
    width: 0;
    height: 0;
    overflow: hidden;
  }

  > h2 {
    font-size: 18px;
    margin-bottom: 24px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  > dl {
    font-size: 15px;

    dt {
      font-weight: bold;
    }

    dd + dt {
      margin-top: 12px;
    }
  }
`

export default AboutReservation
